/**
 * Session approvals — the permission mode plus the tools and risk levels the
 * user has pre-approved — persisted in the THREAD's KV store (server-side), so
 * they resume with the session and a server hook can show the agent what's
 * already approved. Nothing is stored on the client.
 */
import type { ApiClient } from "./api.ts";
import type { PermissionState } from "./permissions.ts";
import type { Level } from "./types.ts";

const KEY = "approvals";

export interface ApprovalsKV {
  level?: Level;
  allowTools: string[];
  allowRisk: number[];
}

export async function loadApprovals(api: ApiClient, threadId: string): Promise<ApprovalsKV> {
  const v = await api.kvGet(threadId, KEY);
  if (v && typeof v === "object" && !Array.isArray(v)) {
    const o = v as Record<string, unknown>;
    const n = Number(o.level);
    const level = n >= 1 && n <= 5 ? (n as Level) : undefined;
    return {
      level,
      allowTools: Array.isArray(o.allowTools) ? (o.allowTools as string[]) : [],
      allowRisk: Array.isArray(o.allowRisk) ? (o.allowRisk as number[]) : [],
    };
  }
  return { allowTools: [], allowRisk: [] };
}

/** Persist the current approvals to the thread KV (fire-and-forget). */
export function saveApprovals(api: ApiClient, threadId: string, perm: PermissionState): void {
  const payload: ApprovalsKV = {
    level: perm.level,
    allowTools: Array.from(perm.alwaysAllow).sort(),
    allowRisk: Array.from(perm.allowRisk).sort((a, b) => a - b),
  };
  void api.kvSet(threadId, KEY, payload);
}
